"use client"

import type React from "react"

interface StatsDisplayProps {
  stats: {
    total: number
    fitness: number
    business: number
    success: number
    golf: number
    eating: number
    calm: number
    humble: number
    study: number
    stress: number
    relationship: number
    general: number
  } | null
  isLoading: boolean
  hasLoaded: boolean
}

export const StatsDisplay: React.FC<StatsDisplayProps> = ({ stats, isLoading, hasLoaded }) => {
  // Show skeleton loading state initially
  if (isLoading && !hasLoaded) {
    return (
      <>
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-number skeleton">
              <div className="skeleton-text">Loading...</div>
            </div>
            <div className="stat-label">Quotes Generated</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">99%</div>
            <div className="stat-label">Satisfaction</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Available</div>
          </div>
        </div>

        <div className="category-stats">
          {[
            { emoji: "🌟", name: "All Quotes" },
            { emoji: "💪", name: "Fitness" },
            { emoji: "💼", name: "Business" },
            { emoji: "🎯", name: "Success" },
            { emoji: "⛳", name: "Golf" },
            { emoji: "🍎", name: "Health" },
            { emoji: "🧘‍♂️", name: "Calm" },
            { emoji: "🙏", name: "Humble" },
            { emoji: "📚", name: "Study" },
            { emoji: "😰", name: "Stress" },
            { emoji: "💕", name: "Relationship" },
          ].map((category, index) => (
            <div key={index} className="category-stat skeleton-stat">
              <span className="category-emoji">{category.emoji}</span>
              <span className="category-count skeleton">
                <div className="skeleton-text">---</div>
              </span>
              <span className="category-name">{category.name}</span>
            </div>
          ))}
        </div>
      </>
    )
  }

  // Show actual stats once loaded
  if (stats) {
    return (
      <>
        <div className="stats-container fade-in">
          <div className="stat-item">
            <div className="stat-number">{stats.total.toLocaleString()}+</div>
            <div className="stat-label">Quotes Generated</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">99%</div>
            <div className="stat-label">Satisfaction</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Available</div>
          </div>
        </div>

        <div className="category-stats fade-in">
            <div className="category-stat">
                <span className="category-emoji">🌟</span>
                <span className="category-count">{stats.total.toLocaleString()}</span>
                <span className="category-name">All Quotes</span>
            </div>
          <div className="category-stat">
            <span className="category-emoji">💪</span>
            <span className="category-count">{stats.fitness.toLocaleString()}</span>
            <span className="category-name">Fitness</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">💼</span>
            <span className="category-count">{stats.business.toLocaleString()}</span>
            <span className="category-name">Business</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">🎯</span>
            <span className="category-count">{stats.success.toLocaleString()}</span>
            <span className="category-name">Success</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">⛳</span>
            <span className="category-count">{stats.golf.toLocaleString()}</span>
            <span className="category-name">Golf</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">🍎</span>
            <span className="category-count">{stats.eating.toLocaleString()}</span>
            <span className="category-name">Health</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">🧘‍♂️</span>
            <span className="category-count">{stats.calm.toLocaleString()}</span>
            <span className="category-name">Calm</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">🙏</span>
            <span className="category-count">{stats.humble.toLocaleString()}</span>
            <span className="category-name">Humble</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">📚</span>
            <span className="category-count">{stats.study.toLocaleString()}</span>
            <span className="category-name">Study</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">😰</span>
            <span className="category-count">{stats.stress.toLocaleString()}</span>
            <span className="category-name">Stress</span>
          </div>
          <div className="category-stat">
            <span className="category-emoji">💕</span>
            <span className="category-count">{stats.relationship.toLocaleString()}</span>
            <span className="category-name">Relationships</span>
          </div>
        </div>
      </>
    )
  }

  // Fallback (shouldn't normally reach here)
  return null
}
